import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import { useDispatch } from 'react-redux';
import OrderListItemMobile from './ui/OrderApproverListItemMobile';
import OrderListItemDesktop from './ui/OrderApproverListItemDesktop';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { calculatePager, Pager } from '../../Services/Pagination.service';
import Pagination from '../Pagination';
import {
  queryUnapprovedOrders,
  setOrder,
  approveOrder,
} from '../../Actions/UnapprovedOrders.action';
import { Order } from '../../Types/Order';
import { useNavigate } from 'react-router-dom';
import SkeletonListItemDesktop from './ui/SkeletonListItemDesktop';
import SkeletonListItemMobile from './ui/SkeletonListItemMobile';

const MyPagesOrderApprover = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orders = useTypedSelector(
    (state) => state.myPage.unapproved_orders || {}
  );
  const { list: orderList, totalCount, errors } = orders;
  const [pager, setPager] = useState<Pager>({});
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [showOnlyMyOrders, setShowOnlyMyOrders] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const generalError = useMemo(
    () =>
      errors && errors['general']?.length ? errors['general'][0] : null,
    [errors]
  );

  useEffect(() => setCurrentPageIndex(1), [showOnlyMyOrders]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      queryUnapprovedOrders(currentPageIndex, showOnlyMyOrders)
    ).finally(() => setIsLoading(false));
  }, [dispatch, currentPageIndex, showOnlyMyOrders]);

  useEffect(() => {
    setPager(calculatePager(totalCount, currentPageIndex));
  }, [totalCount, currentPageIndex]);

  const onOrderClick = (order: Order) => {
    dispatch(setOrder(order));
    navigate(order.externalOrderID + '/' + false);
  };

  const onApproveOrderClik = async (order: Order) => {
    setIsLoading(true);
    const data = await approveOrder(order.orderId);
    setCurrentPageIndex(1);
    dispatch(
      queryUnapprovedOrders(currentPageIndex, showOnlyMyOrders)
    ).finally(() => setIsLoading(false));
  };

  return (
    <div className="col-span-3">
      <h1 className="mb-2">{translate('mypage.order.title')}</h1>
      <p className="mb-4 max-w-3xl text-lg lg:mb-6">
        {translate('mypage.orderapprover.text') !==
          'mypage.orderapprover.text' &&
          translate('mypage.orderapprover.text')}
      </p>
      {generalError && <p className="error">{generalError}</p>}
      <button
        className="btn btn--outline btn--medium mb-4"
        onClick={() => setShowOnlyMyOrders(!showOnlyMyOrders)}
      >
        {!showOnlyMyOrders
          ? translate('mypage.order.show-only-my-orders')
          : translate('mypage.order.show-organization-orders')}
      </button>
      {!isLoading && (!orderList || orderList.length == 0) && (
        <p>{translate('mypage.order.no-orders')}</p>
      )}
      <ul className="lg:hidden">
        <SkeletonListItemMobile items={3} cells={5} loading={!orderList} />
        {orderList &&
          orderList.map((order) => (
            <OrderListItemMobile
              order={order}
              key={order.externalOrderID + 'mobile'}
              onClick={onOrderClick}
              onClickApprove={onApproveOrderClik}
            />
          ))}
      </ul>
      <ul className="hidden rounded border border-solid border-gray-300 bg-white lg:block">
        <div className="grid grid-cols-6 gap-4 border-b border-solid border-gray-300 p-2">
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
            {translate('general.ordernumber')}
          </p>
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
            {translate('general.quantity')}
          </p>
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
            {translate('orderdetail.orderdate')}
          </p>
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
            {translate('orderdetail.grandtotal')}
          </p>
        </div>
        <SkeletonListItemDesktop
          items={5}
          cells={6}
          loading={!orderList}
        />
        {orderList &&
          orderList.map((order) => (
            <OrderListItemDesktop
              order={order}
              key={order.externalOrderID + 'desktop'}
              onClick={onOrderClick}
              onClickApprove={onApproveOrderClik}
            />
          ))}
      </ul>
      <div className="mt-4">
        <Pagination
          model={pager}
          onChange={(index) =>
            index !== currentPageIndex && setCurrentPageIndex(index)
          }
        />
      </div>
    </div>
  );
};

export default MyPagesOrderApprover;
